import { useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  createOnsiteLiveUser,
  getOnsiteLiveUser,
  listOnsiteLiveUsers,
  setOnsiteLiveMemberships,
  updateOnsiteLiveUser,
} from '@/api/onsiteLiveAdmin';
import { PersonPicker } from '@/components/common/assignPickers';
import { formatDate } from '@/lib/dateFormat';
import type { OnsiteLiveLookups, OnsiteLiveMemberships, OnsiteLiveUser } from '@/types/oscar';
import { emptyUser, sameIds, toggleId, toUserPayload, userSchema, userToForm, type UserFormData } from './schema';
import { OnsiteLiveUserPicker } from './OnsiteLiveUserPicker';
import { MailRecipientPicker } from './MailRecipientPicker';
import { AdditionalSupervisorsEditor } from './AdditionalSupervisorsEditor';
import { ErrorBanner, FieldError } from '../BranchRelationships/shared';
import { btnPrimary, btnSecondary, inputCls } from '../BranchRelationships/styles';

type Selected = { mode: 'edit'; id: number } | { mode: 'new' } | null;

/**
 * Onsite Users — OnBudget's "Onsite Users" screen. Left: search the accounts.
 * Right: the account (the legacy form, field for field) and what it belongs to
 * (security groups, companies, subscription groups).
 *
 * ⚠ Ids are the LEGACY ids (UserId in OnBudget), kept so nothing on SQ04 moves.
 */
export function UsersTab({ lookups, canWrite }: { lookups: OnsiteLiveLookups | undefined; canWrite: boolean }) {
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  // ?user=<UserId> opens that account (the link on a person's OnsiteLive tab).
  const [selected, setSelected] = useState<Selected>(() => {
    const id = Number(new URLSearchParams(window.location.search).get('user'));
    return Number.isInteger(id) && id > 0 ? { mode: 'edit', id } : null;
  });

  const usersQuery = useQuery({
    queryKey: ['onsitelive-admin', 'users', { search: search.trim(), includeInactive }],
    queryFn: ({ signal }) => listOnsiteLiveUsers({ search, includeInactive }, signal),
  });
  const users = usersQuery.data ?? [];

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="lg:w-80 lg:shrink-0">
        <div className="flex items-end gap-2">
          <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
            Search
            <input
              aria-label="Search OnsiteLive users"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Username, name or email"
              className={`${inputCls} mt-1 font-normal`}
            />
          </label>
          {canWrite && (
            <button type="button" className={btnSecondary} onClick={() => setSelected({ mode: 'new' })}>
              New user
            </button>
          )}
        </div>
        <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-darkGrey">
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          Include inactive
        </label>

        {usersQuery.isError && <ErrorBanner message={(usersQuery.error as Error).message || 'Could not load users.'} />}
        <ul className="mt-2 max-h-[70vh] space-y-1 overflow-y-auto" aria-label="OnsiteLive users">
          {usersQuery.isLoading && <li className="text-sm text-darkGrey/60">Loading…</li>}
          {!usersQuery.isLoading && users.length === 0 && <li className="text-sm text-darkGrey/60">Nobody matches.</li>}
          {users.map((u) => {
            const isSel = selected?.mode === 'edit' && selected.id === u.OnsiteLiveUserId;
            return (
              <li key={u.OnsiteLiveUserId}>
                <button
                  type="button"
                  onClick={() => setSelected({ mode: 'edit', id: u.OnsiteLiveUserId })}
                  aria-current={isSel ? 'true' : undefined}
                  className={`w-full rounded border px-2 py-1.5 text-left text-sm ${isSel ? 'border-navy bg-[#E6ECF2]' : 'border-border bg-white hover:bg-[#F4F6F9]'}`}
                >
                  <span className="font-medium text-navy">{u.Username}</span>
                  {!u.Active && <span className="ml-2 rounded-full bg-darkGrey/10 px-1.5 text-[10px] font-semibold text-darkGrey/70">Inactive</span>}
                  <span className="block text-xs text-darkGrey/70">
                    {u.FullName ?? '—'}{u.Branch ? ` · ${u.Branch}` : ''}{u.TeamCode ? ` · ${u.TeamCode}` : ''}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {users.length >= 500 && <p className="mt-1 text-xs text-darkGrey/60">Showing the first 500 — narrow the search.</p>}
      </div>

      <div className="min-w-0 flex-1">
        {selected === null && (
          <p className="rounded border border-dashed border-border bg-white px-4 py-6 text-sm text-darkGrey/70">
            Pick an account on the left{canWrite ? ', or add a new one' : ''}.
          </p>
        )}
        {selected?.mode === 'new' && (
          <UserEditor
            key="new"
            user={null}
            lookups={lookups}
            canWrite={canWrite}
            onSaved={(u) => setSelected({ mode: 'edit', id: u.OnsiteLiveUserId })}
          />
        )}
        {selected?.mode === 'edit' && <UserLoader key={selected.id} id={selected.id} lookups={lookups} canWrite={canWrite} />}
      </div>
    </div>
  );
}

/**
 * One account. A person's account is edited on their Oscar person page (Person ›
 * OnsiteLive) - one place per person - so here it is read only, with a link.
 * Accounts with no person (shared branch logins, service accounts, historical
 * leavers) are edited here, which is also where one is linked to a person.
 */
function UserLoader({ id, lookups, canWrite }: { id: number; lookups: OnsiteLiveLookups | undefined; canWrite: boolean }) {
  const q = useQuery({ queryKey: ['onsitelive-admin', 'user', id], queryFn: ({ signal }) => getOnsiteLiveUser(id, signal) });
  if (q.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (q.isError || !q.data) return <ErrorBanner message={(q.error as Error | null)?.message || 'Could not load the user.'} />;
  const personId = q.data.PersonId;
  const editHere = canWrite && personId === null;
  return (
    <div className="space-y-4">
      {personId !== null && (
        <p role="status" className="rounded border border-navy/20 bg-[#E6ECF2] px-3 py-2 text-sm text-darkGrey">
          This account belongs to {q.data.PersonDisplayName ?? `person ${personId}`}. Its OnsiteLive settings are edited on{' '}
          <Link to={`/people/${personId}?tab=onsitelive`} className="font-semibold text-navy hover:underline">their Oscar person page</Link>.
        </p>
      )}
      <UserEditor user={q.data} lookups={lookups} canWrite={editHere} />
      <AdditionalSupervisorsEditor userId={q.data.OnsiteLiveUserId} username={q.data.Username} canWrite={editHere} />
      <MembershipsEditor user={q.data} lookups={lookups} canWrite={editHere} />
    </div>
  );
}

/**
 * The account form. `onPerson` = shown on the Oscar person page: the login and
 * the person / system-account link are not edited there (they ARE the person),
 * the branch is the Profile's primary branch, and email divert + replacement
 * manager are on the person's Leaving tab (Anthony, 25/10/2026).
 */
export function UserEditor({
  user, lookups, canWrite, onSaved, onPerson = false,
}: {
  user: OnsiteLiveUser | null;
  lookups: OnsiteLiveLookups | undefined;
  canWrite: boolean;
  onSaved?: (u: OnsiteLiveUser) => void;
  onPerson?: boolean;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [personName, setPersonName] = useState(user?.PersonDisplayName ?? '');
  const { register, handleSubmit, formState, reset, setValue, watch } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: user ? userToForm(user) : emptyUser,
  });
  useEffect(() => { reset(user ? userToForm(user) : emptyUser); }, [user, reset]);

  const save = useMutation({
    mutationFn: (d: UserFormData) => {
      const body = toUserPayload(d);
      return user ? updateOnsiteLiveUser(user.OnsiteLiveUserId, body) : createOnsiteLiveUser(body);
    },
    onSuccess: (u) => {
      setError(null);
      setSavedAt(new Date().toISOString());
      qc.setQueryData(['onsitelive-admin', 'user', u.OnsiteLiveUserId], u);
      void qc.invalidateQueries({ queryKey: ['onsitelive-admin', 'users'] });
      onSaved?.(u);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save the user.'),
  });
  const err = formState.errors;
  const personId = watch('PersonId');
  const teamId = watch('TeamId');
  const extManagerId = watch('ExtManagerId');
  const replacementId = watch('ReplacementManagerUserId');
  const divert = watch('EmailDivert');
  // The person sync owns these: typed values would be reverted within 15 minutes.
  const owned = new Set(user && String(user.PersonId ?? '') === personId ? user.PersonOwnedFields : []);
  const fromPerson = 'From the Oscar person (kept in step every 15 minutes)';

  return (
    <form
      className="rounded border border-border bg-white p-4"
      onSubmit={(e) => { e.preventDefault(); void handleSubmit((d) => save.mutate(d))(); }}
      aria-label={user ? `OnsiteLive user ${user.Username}` : 'New OnsiteLive user'}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-heading text-lg font-bold text-navy">
          {user ? (onPerson ? `OnsiteLive permissions — ${user.Username}` : user.Username) : 'New OnsiteLive user'}
          {user && <span className="ml-2 font-mono text-xs font-normal text-darkGrey/60">UserId {user.OnsiteLiveUserId}</span>}
        </h2>
        {user && (
          <p className="text-xs text-darkGrey/60">
            Added {formatDate(user.CreatedAt)} by {user.CreatedBy}
            {user.ModifiedAt && ` · changed ${formatDate(user.ModifiedAt)} by ${user.ModifiedBy ?? 'unknown'}`}
          </p>
        )}
      </div>

      <fieldset disabled={!canWrite || save.isPending} className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* The details. On the person page they are the Profile's, so they are not shown again. */}
        {!onPerson && (
          <>
            <Field label="Username" error={err.Username?.message} reg={register('Username')} />
            <Field label="Full name" error={err.FullName?.message} reg={register('FullName')} locked={owned.has('FullName') ? fromPerson : null} />
            <Field label="Title" error={err.Title?.message} reg={register('Title')} locked={owned.has('Title') ? fromPerson : null} />
            <Field label="Email address" error={err.EmailAddress?.message} reg={register('EmailAddress')} type="email" locked={owned.has('EmailAddress') ? fromPerson : null} />
            <Field label="Mobile" error={err.Mobile?.message} reg={register('Mobile')} locked={owned.has('Mobile') ? fromPerson : null} />
            <Field label="Phone" error={err.Phone?.message} reg={register('Phone')} locked={owned.has('Phone') ? fromPerson : null} />
            <Field
              label="Office location"
              error={err.OfficeLocation?.message}
              reg={register('OfficeLocation')}
              locked={owned.has('OfficeLocation') ? "From the Oscar person's location" : null}
            />
          </>
        )}

        {/* The person's primary branch (Profile) when linked; otherwise an OnsiteLive setting. */}
        {!onPerson && (
          <Field
            label="Branch"
            error={err.Branch?.message}
            reg={register('Branch')}
            locked={owned.has('Branch') ? "The Oscar person's primary branch" : null}
          />
        )}
        <Select
          label="Team"
          reg={register('TeamId')}
          options={(lookups?.Teams ?? []).map((t) => [t.Id, `${t.Id} — ${t.Name}`])}
          hint={user?.RuleTeamId != null
            ? `Department rule: ${user.RuleTeamId} — ${user.RuleTeamCode ?? ''}${String(user.RuleTeamId) === teamId ? ' (matches)' : ' (differs — see Teams and limits)'}`
            : null}
        />
        <Select
          label="Subscription branch"
          reg={register('SubscriptionBranch')}
          options={(lookups?.BranchSets ?? []).map((b) => [b.SubBranchid, `${b.SubBranchid} — ${b.Description ?? ''}`])}
        />
        <Select
          label="Business unit"
          reg={register('BusinessUnitBranchId')}
          options={(lookups?.BranchSets ?? []).map((b) => [b.SubBranchid, `${b.SubBranchid} — ${b.Description ?? ''}`])}
        />
        {!onPerson && (
          <>
            <div className="flex flex-col text-xs font-semibold text-darkGrey">
              <label className="flex flex-col">
                <span>Expiry date <span className="font-normal text-darkGrey/60">(offboarding disables the account after this)</span></span>
                <input
                  type="date"
                  {...register('ExpiryDate')}
                  readOnly={owned.has('ExpiryDate')}
                  className={`${inputCls} mt-1 font-normal ${owned.has('ExpiryDate') ? 'bg-[#F4F6F9] text-darkGrey/80' : ''}`}
                />
              </label>
              {owned.has('ExpiryDate') && <span className="mt-1 font-normal text-darkGrey/70">The Oscar person&apos;s termination date</span>}
            </div>
            <Field
              label="Manager (UserId)"
              error={err.ManagerId?.message}
              reg={register('ManagerId')}
              hint={user?.ManagerName}
              inputMode="numeric"
              locked={owned.has('ManagerId') ? "From the Oscar person's manager" : null}
            />
          </>
        )}
        <OnsiteLiveUserPicker
          label="Manager if none set"
          value={extManagerId}
          valueLabel={user?.ExtManagerName}
          onChange={(v) => setValue('ExtManagerId', v, { shouldDirty: true })}
          canWrite={canWrite}
          excludeId={user?.OnsiteLiveUserId}
          hint="OnsiteLive's External manager. OnFlow uses it ONLY when no manager is set (PO and SSA approvals). Extra approvers go under Additional supervisors."
          error={err.ExtManagerId?.message}
        />
        {!onPerson && (
          <>
            <OnsiteLiveUserPicker
              label="Replacement manager"
              value={replacementId}
              valueLabel={user?.ReplacementManagerName}
              onChange={(v) => setValue('ReplacementManagerUserId', v, { shouldDirty: true })}
              canWrite={canWrite}
              excludeId={user?.OnsiteLiveUserId}
              error={err.ReplacementManagerUserId?.message}
            />
            <MailRecipientPicker
              value={divert}
              onChange={(v) => setValue('EmailDivert', v, { shouldDirty: true, shouldValidate: true })}
              canWrite={canWrite}
              error={err.EmailDivert?.message}
            />
          </>
        )}

        {!onPerson && (
          <div className="flex flex-col text-xs font-semibold text-darkGrey">
            Oscar person
            <div className="mt-1 font-normal">
              {canWrite ? (
                <PersonPicker
                  label="Oscar person"
                  value={personId}
                  onChange={(v, name) => { setValue('PersonId', v, { shouldDirty: true }); setPersonName(name ?? ''); }}
                />
              ) : (
                <span className="text-sm">{personName || '—'}</span>
              )}
            </div>
            {canWrite && personId && personName && <span className="mt-1 font-normal text-darkGrey/70">{personName}</span>}
            {canWrite && personId && (
              <span className="mt-1 font-normal text-darkGrey/70">
                Name, email, title, mobile and manager are copied from the person when you save. One OnsiteLive user per person.
              </span>
            )}
          </div>
        )}
        {!onPerson && (
          <Field
            label="System account (id)"
            error={err.SystemAccountId?.message}
            reg={register('SystemAccountId')}
            hint={user?.SystemAccountName}
            inputMode="numeric"
          />
        )}

        <label className="inline-flex items-center gap-2 text-sm font-semibold text-darkGrey md:col-span-2">
          <input type="checkbox" {...register('Active')} />
          Active — the account can sign in to OnsiteLive, OnBudget and OnFlow
        </label>
      </fieldset>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {canWrite && (
        <div className="mt-4 flex items-center gap-3">
          <button type="submit" className={btnPrimary} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : user ? 'Save changes' : 'Add user'}
          </button>
          {savedAt && !save.isPending && <span className="text-xs text-green" role="status">Saved.</span>}
        </div>
      )}
    </form>
  );
}

/** Security groups, companies and subscription groups: tick, then save once. */
export function MembershipsEditor({ user, lookups, canWrite }: { user: OnsiteLiveUser; lookups: OnsiteLiveLookups | undefined; canWrite: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<OnsiteLiveMemberships>(user.Memberships);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  useEffect(() => { setDraft(user.Memberships); }, [user.Memberships]);

  const dirty = !sameIds(draft.SecurityGroupIds, user.Memberships.SecurityGroupIds)
    || !sameIds(draft.CompanyIds, user.Memberships.CompanyIds)
    || !sameIds(draft.SubscriptionGroupIds, user.Memberships.SubscriptionGroupIds);

  const save = useMutation({
    mutationFn: () => setOnsiteLiveMemberships(user.OnsiteLiveUserId, draft),
    onSuccess: (m) => {
      setError(null);
      qc.setQueryData<OnsiteLiveUser>(['onsitelive-admin', 'user', user.OnsiteLiveUserId], (old) => (old ? { ...old, Memberships: m } : old));
      void qc.invalidateQueries({ queryKey: ['onsitelive-admin', 'lookups'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save the memberships.'),
  });

  const term = filter.trim().toLowerCase();
  const groups = useMemo(
    () => (lookups?.SecurityGroups ?? []).filter((g) => !term || `${g.Name} ${g.Description ?? ''}`.toLowerCase().includes(term)),
    [lookups, term],
  );

  return (
    <section className="rounded border border-border bg-white p-4" aria-label="Memberships">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-heading text-base font-bold text-navy">What {user.Username} can see</h3>
        <input
          aria-label="Filter security groups"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter security groups"
          className={`${inputCls} w-56`}
        />
      </div>

      <fieldset disabled={!canWrite || save.isPending} className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
        <CheckList
          title="Security groups (reports)"
          items={groups.map((g) => ({ id: g.ReportSecurityGroupId, label: g.Name, muted: !g.Active }))}
          checked={draft.SecurityGroupIds}
          onToggle={(id) => setDraft((d) => ({ ...d, SecurityGroupIds: toggleId(d.SecurityGroupIds, id) }))}
        />
        <CheckList
          title="Companies"
          items={(lookups?.Companies ?? []).map((c) => ({ id: c.Id, label: c.Name }))}
          checked={draft.CompanyIds}
          onToggle={(id) => setDraft((d) => ({ ...d, CompanyIds: toggleId(d.CompanyIds, id) }))}
        />
        <CheckList
          title="Subscription groups"
          items={(lookups?.SubscriptionGroups ?? []).map((g) => ({ id: g.SubGpId, label: g.Name ?? `#${g.SubGpId}`, muted: g.Inactive }))}
          checked={draft.SubscriptionGroupIds}
          onToggle={(id) => setDraft((d) => ({ ...d, SubscriptionGroupIds: toggleId(d.SubscriptionGroupIds, id) }))}
        />
      </fieldset>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {canWrite && (
        <div className="mt-4 flex items-center gap-3">
          <button type="button" className={btnPrimary} disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save memberships'}
          </button>
          {dirty && (
            <button type="button" className={btnSecondary} onClick={() => setDraft(user.Memberships)} disabled={save.isPending}>
              Undo
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function CheckList({
  title, items, checked, onToggle,
}: {
  title: string;
  items: { id: number; label: string; muted?: boolean }[];
  checked: number[];
  onToggle: (id: number) => void;
}) {
  return (
    <div>
      <p className="text-xs font-semibold text-darkGrey">
        {title} <span className="font-normal text-darkGrey/60">({checked.length})</span>
      </p>
      <ul className="mt-1 max-h-72 space-y-0.5 overflow-y-auto rounded border border-border p-2">
        {items.length === 0 && <li className="text-xs text-darkGrey/60">None.</li>}
        {items.map((it) => (
          <li key={it.id}>
            <label className={`inline-flex items-center gap-1.5 text-sm ${it.muted ? 'text-darkGrey/50' : 'text-darkGrey'}`}>
              <input type="checkbox" checked={checked.includes(it.id)} onChange={() => onToggle(it.id)} />
              {it.label}
              <span className="font-mono text-[10px] text-darkGrey/50">{it.id}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Field({
  label, reg, error, hint, type = 'text', inputMode, locked,
}: {
  label: string;
  reg: UseFormRegisterReturn;
  error?: string;
  hint?: string | null;
  type?: string;
  inputMode?: 'numeric';
  /** Why the field is read-only (the Oscar person owns it); null/absent = editable. */
  locked?: string | null;
}) {
  // The notes sit outside the <label> so the field's accessible name stays its label.
  return (
    <div className="flex flex-col text-xs font-semibold text-darkGrey">
      <label className="flex flex-col">
        {label}
        <input
          type={type}
          inputMode={inputMode}
          {...reg}
          readOnly={!!locked}
          className={`${inputCls} mt-1 font-normal ${locked ? 'bg-[#F4F6F9] text-darkGrey/80' : ''}`}
        />
      </label>
      {locked && <span className="mt-1 font-normal text-darkGrey/70">{locked}</span>}
      {hint && <span className="mt-1 font-normal text-darkGrey/70">{hint}</span>}
      {error && <FieldError message={error} />}
    </div>
  );
}

function Select({
  label, reg, options, hint,
}: {
  label: string;
  reg: UseFormRegisterReturn;
  options: [number, string][];
  hint?: string | null;
}) {
  return (
    <div className="flex flex-col text-xs font-semibold text-darkGrey">
      <label className="flex flex-col">
        {label}
        <select {...reg} className={`${inputCls} mt-1 font-normal`}>
          <option value="">— none —</option>
          {options.map(([id, text]) => <option key={id} value={String(id)}>{text}</option>)}
        </select>
      </label>
      {hint && <span className="mt-1 font-normal text-darkGrey/70">{hint}</span>}
    </div>
  );
}
